/* eslint-disable @typescript-eslint/ban-ts-comment */
import UnauthorizedPage from 'components/UnauthorizedPage'
import usePermissions from 'hooks/usePermissions'
import { useFilters } from 'hooks/useQuery'
import useScreenSizeStyle from 'hooks/useScreenSizeStyle'
import { useLoadCustomers } from 'queries/customers/useLoadCustomer'
import * as React from 'react'
import { formatPhoneNumber } from 'react-phone-number-input'
import { Link } from 'react-router-dom'
import Pagination from 'ui/atoms/Pagination'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import { buildPhoneNumberValue } from 'ui/SingleLinePhone'
import { getFormattedDate } from 'utils/dates'

import { EditableBadge } from '@/components/Badges/EditableBadge'
import { Customer } from '@/types/customer'

function ClientsTable() {
  const textSize = useScreenSizeStyle({
    md: { small: true, xsmall: false },
    generic: { small: false, xsmall: true },
  })
  const maxWidthText = useScreenSizeStyle({
    md: '170px',
    generic: '110px',
  })

  const { filters } = useFilters({ defaultValues: { page: 1, pageSize: 25 } })
  const { data, isLoading, isFetching } = useLoadCustomers(filters)
  const fetchingClients = isLoading || isFetching

  const { canViewClientProfile } = usePermissions()

  const ClientLink = React.useCallback(
    ({ client, children }: { client: Customer; children: React.ReactNode }) => {
      return (
        <Link className="hover:no-underline" to={`/clients/${client.id}`}>
          {children}
        </Link>
      )
    },
    []
  )

  if (!canViewClientProfile) return <UnauthorizedPage />

  return (
    <div className="pb-2">
      <Table>
        <Table.Head
          columns={[
            '#',
            'FIRST NAME',
            'LAST NAME',
            'EMAIL',
            'PHONE',
            'DATE OF BIRTH',
            'TAGS',
          ]}
          orderSet={[
            { column: '#', key: 'id' },
            { column: 'FIRST NAME', key: 'first_name' },
            { column: 'LAST NAME', key: 'last_name' },
            { column: 'EMAIL', key: 'email' },
            { column: 'PHONE', key: 'phone_number' },
            { column: 'DATE OF BIRTH', key: 'date_birth' },
            { column: 'TAGS', key: 'tags' },
          ]}
        />

        <Table.Body>
          {fetchingClients && (
            <Table.Row>
              <Table.Column>
                <Text>Loading...</Text>
              </Table.Column>
              <Table.Column />
              <Table.Column />
              <Table.Column />
              <Table.Column />
              <Table.Column />
              <Table.Column />
            </Table.Row>
          )}

          {!fetchingClients && data?.results.length === 0 && (
            <Table.Row>
              <Table.Column>
                <Text>No Results Found</Text>
              </Table.Column>
              <Table.Column />
              <Table.Column />
              <Table.Column />
              <Table.Column />
              <Table.Column />
              <Table.Column />
            </Table.Row>
          )}

          {!fetchingClients &&
            data?.results.map((client) => (
              <Table.Row
                className="cursor-pointer border-b border-gray-200 hover:bg-gray-50"
                key={client.id}
              >
                <Table.Column>
                  <ClientLink client={client}>
                    <Text
                      $truncate
                      // @ts-ignore
                      $small={textSize?.small}
                      // @ts-ignore
                      $xsmall={textSize?.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {client.id}
                    </Text>
                  </ClientLink>
                </Table.Column>
                <Table.Column>
                  <ClientLink client={client}>
                    <Text
                      $truncate
                      // @ts-ignore
                      $small={textSize?.small}
                      // @ts-ignore
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {client.first_name || '-'}
                    </Text>
                  </ClientLink>
                </Table.Column>
                <Table.Column>
                  <ClientLink client={client}>
                    <Text
                      $truncate
                      // @ts-ignore
                      $small={textSize?.small}
                      // @ts-ignore
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {client.last_name || '-'}
                    </Text>
                  </ClientLink>
                </Table.Column>
                <Table.Column>
                  <ClientLink client={client}>
                    <Text
                      $truncate
                      // @ts-ignore
                      $small={textSize?.small}
                      // @ts-ignore
                      $xsmall={textSize.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {client.email || '-'}
                    </Text>
                  </ClientLink>
                </Table.Column>
                <Table.Column>
                  <ClientLink client={client}>
                    <Text
                      $truncate
                      // @ts-ignore
                      $small={textSize?.small}
                      // @ts-ignore
                      $xsmall={textSize?.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {formatPhoneNumber(
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        buildPhoneNumberValue(client?.phone_number)
                      ) || '-'}
                    </Text>
                  </ClientLink>
                </Table.Column>
                <Table.Column>
                  <ClientLink client={client}>
                    <Text
                      $truncate
                      // @ts-ignore
                      $small={textSize?.small}
                      // @ts-ignore
                      $xsmall={textSize?.xsmall}
                      style={{ maxWidth: maxWidthText }}
                    >
                      {getFormattedDate(
                        client.date_birth,
                        'MM-DD-YYYY',
                        true
                      ) || '-'}
                    </Text>
                  </ClientLink>
                </Table.Column>
                <Table.Column>
                  <ClientLink client={client}>
                    <EditableBadge.Root maxVisible={2}>
                      {client?.customer_tags?.map((item) => (
                        <EditableBadge.RawItem color={item.color} key={item.id}>
                          {item.name}
                        </EditableBadge.RawItem>
                      ))}
                    </EditableBadge.Root>
                  </ClientLink>
                </Table.Column>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
      <Pagination.Border>
        <Pagination total={data?.count || 0} />
      </Pagination.Border>
    </div>
  )
}

export default ClientsTable
